import React, { useMemo } from 'react';
import { useParams, useNavigate, useSearchParams, useLocation } from 'react-router-dom';
import { ArrowLeft, List, Loader2 } from 'lucide-react';
import {
  MetricsRow, CompetencyChart, SubjectBreakdownPie,
  TopPerformersCard, AlertsCard, DrillCard,
} from '../../../components/LevelDashboard';
import type { SubjectBucket } from '../../../components/LevelDashboard';
import type { LevelMetrics, CompetencyBucket, TopPerformer, AlertItem } from '../../../types';
import {
  useGetKvsResult9n11RoDetailsQuery,
  useGetKvsResult9n11SubjectwiseQuery,
} from '../../../services/kvsResult9n11Api';
import SchoolPassPercentTable from '../../../components/SchoolPassPercentTable';
import type { SchoolPassRow } from '../../../components/SchoolPassPercentTable';

const STAGE_CLASSES = [9, 11];

const BASE = '/kvs-samagam/student-analytics/stages/secondary';
const levelSlug = (level: string) => level.toLowerCase().replace(/\s+/g, '-');

const RegionDrillDown: React.FC = () => {
  const { regionId } = useParams<{ regionId: string }>();
  const [searchParams] = useSearchParams();
  const navigate = useNavigate();
  const location = useLocation();
  const academicYear = Number(searchParams.get('year') || '2025');
  // Class 9/11 results carry no term dimension — always query "overall" (0).
  const term = 0;
  const querySuffix = `?year=${academicYear}`;
  const numericRegionId = Number(regionId);

  const { data: roDetailsRes, isLoading: isRoLoading } = useGetKvsResult9n11RoDetailsQuery(
    !Number.isNaN(numericRegionId) ? { year: academicYear, term, roid: numericRegionId } : { year: academicYear, term, roid: 0 }
  );

  const { data: subjectRes, isLoading: isSubjectLoading } = useGetKvsResult9n11SubjectwiseQuery({ year: academicYear, term });

  const isLoading = isRoLoading || isSubjectLoading;

  const roData = roDetailsRes?.data;
  const regionName = roData?.roName || `Region ${regionId}`;
  const schools = roData?.schools || [];

  const metrics: LevelMetrics = useMemo(() => {
    if (!roData || schools.length === 0) {
      return { totalStudents: 0, avgAttendance: 0, avgCompetency: 0, needsSupportCount: 0, excellentCount: 0, avgBehaviorScore: 0, avgPeerScore: 0, bmiNormalPercent: 0 };
    }
    const totalStudents = schools.reduce((sum, s) => sum + s.totalStudents, 0);
    const weightedMarks = schools.reduce((sum, s) => sum + (s.avgMarks * s.totalStudents), 0);
    const avgCompetency = totalStudents > 0 ? +(weightedMarks / totalStudents).toFixed(1) : 0;
    const needsSupportCount = schools.reduce((sum, s) => sum + s.needSupport, 0);
    const excellentCount = schools.reduce((sum, s) => sum + s.excellentStudents, 0);
    return {
      totalStudents,
      avgAttendance: 0,
      avgCompetency,
      needsSupportCount,
      excellentCount,
      avgBehaviorScore: 0,
      avgPeerScore: 0,
      bmiNormalPercent: 0,
    };
  }, [roData, schools]);

  const competencyBuckets: CompetencyBucket[] = useMemo(() => {
    if (!roData || schools.length === 0) {
      return [
        { level: 'Excellent', count: 0, percent: 0 },
        { level: 'Good', count: 0, percent: 0 },
        { level: 'Average', count: 0, percent: 0 },
        { level: 'Below Average', count: 0, percent: 0 },
      ];
    }
    const total = schools.reduce((sum, s) => sum + s.totalStudents, 0) || 1;
    const excellent = schools.reduce((sum, s) => sum + s.excellentStudents, 0);
    const belowAverage = schools.reduce((sum, s) => sum + s.needSupport, 0);
    const remaining = Math.max(0, total - excellent - belowAverage);
    const good = Math.round(remaining * 0.4);
    const average = remaining - good;

    return [
      { level: 'Excellent', count: excellent, percent: +((excellent / total) * 100).toFixed(1) },
      { level: 'Good', count: good, percent: +((good / total) * 100).toFixed(1) },
      { level: 'Average', count: average, percent: +((average / total) * 100).toFixed(1) },
      { level: 'Below Average', count: belowAverage, percent: +((belowAverage / total) * 100).toFixed(1) },
    ];
  }, [roData, schools]);

  const subjectData: SubjectBucket[] = useMemo(() => {
    const items = subjectRes?.data?.overall || [];
    // The 9/11 source has no per-subject marks; skip the "Overall" placeholder
    // row so the card stays hidden until real subjects exist.
    return items
      .filter(item => item.subjectTitle !== 'Overall')
      .map(item => ({ subject: item.subjectTitle, avgScore: +item.avgWeightage.toFixed(1) }));
  }, [subjectRes]);

  const topPerformers: TopPerformer[] = useMemo(() => {
    return [...schools]
      .sort((a, b) => b.avgMarks - a.avgMarks)
      .slice(0, 5)
      .map((s, i) => ({
        id: `top-${s.schoolID}-${i}`,
        name: s.schoolName,
        metric: 'Avg Marks',
        value: s.avgMarks,
      }));
  }, [schools]);

  const alertItems: AlertItem[] = useMemo(() => {
    return [...schools]
      .sort((a, b) => a.avgMarks - b.avgMarks)
      .slice(0, 5)
      .map((s, i) => ({
        id: `alert-${s.schoolID}-${i}`,
        name: s.schoolName,
        issue: `Avg ${s.avgMarks}% — ${s.totalStudents.toLocaleString()} students`,
        severity: s.avgMarks < 60 ? 'high' : s.avgMarks < 75 ? 'medium' : 'low',
      }));
  }, [schools]);

  if (!regionId || Number.isNaN(numericRegionId)) {
    return (
      <div className="p-6 text-center">
        <p className="text-gray-500">Region not found.</p>
        <button onClick={() => navigate(-1)} className="text-indigo-600 text-sm mt-2 hover:underline">Go back</button>
      </div>
    );
  }

  if (isLoading) {
    return (
      <div className="flex items-center justify-center min-h-[400px]">
        <Loader2 className="w-8 h-8 animate-spin text-indigo-500" />
        <span className="ml-3 text-gray-500">Loading region data…</span>
      </div>
    );
  }

  return (
    <div className="p-6 max-w-[1600px] mx-auto space-y-6">
      <div className="flex items-center gap-3">
        <button onClick={() => navigate(location.pathname.slice(0, location.pathname.lastIndexOf(`/region/${regionId}`)) + location.search)} className="p-2 rounded-lg hover:bg-gray-100 text-gray-500">
          <ArrowLeft size={20} />
        </button>
        <div>
          <h1 className="text-2xl font-bold text-gray-900">{regionName} Region</h1>
          <p className="text-sm text-gray-500">Secondary Stage &mdash; Academic Year {academicYear} &mdash; {metrics.totalStudents.toLocaleString()} students &mdash; {schools.length} schools</p>
        </div>
      </div>

      <MetricsRow m={metrics} />

      <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
        <CompetencyChart buckets={competencyBuckets} onBarClick={(lvl) => navigate(`${BASE}/region/${regionId}/list/schools/${levelSlug(lvl)}${querySuffix}`)} />
        {subjectData.length > 0 && (
          <SubjectBreakdownPie data={subjectData} title="Subject-wise Avg Weightage" />
        )}
      </div>

      <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
        <TopPerformersCard items={topPerformers} />
        <AlertsCard items={alertItems} onViewAll={() => navigate(`${BASE}/school-performance${querySuffix}`)} viewAllLabel="Need Attention Schools" />
      </div>

      <SchoolPassPercentTable
        schools={schools as SchoolPassRow[]}
        classes={STAGE_CLASSES}
        stageLabel="Secondary Stage"
        regionName={regionName}
        year={academicYear}
        onRowClick={(s) => navigate(`${BASE}/region/${regionId}/school/${s.schoolID ?? s.kvID}${querySuffix}`)}
      />

      <div>
        <div className="flex items-center justify-between mb-3">
          <h2 className="text-lg font-semibold text-gray-900">Schools — Drill Down</h2>
          <button onClick={() => navigate(`${BASE}/region/${regionId}/list/schools${querySuffix}`)} className="inline-flex items-center gap-1.5 text-sm font-medium text-indigo-600 hover:text-indigo-800 transition">
            <List size={16} /> See All Schools
          </button>
        </div>
        <div className="grid grid-cols-1 md:grid-cols-2 xl:grid-cols-3 gap-4">
          {schools.map((s, i) => (
            <DrillCard
              key={`${s.schoolID}-${i}`}
              title={s.schoolName}
              subtitle={`${s.totalStudents.toLocaleString()} students`}
              metrics={{
                totalStudents: s.totalStudents, avgAttendance: 0, avgCompetency: s.avgMarks,
                needsSupportCount: s.needSupport, excellentCount: s.excellentStudents, avgBehaviorScore: 0, avgPeerScore: 0, bmiNormalPercent: 0,
              }}
              alerts={[]}
              navigateTo={`school/${s.schoolID}${querySuffix}`}
            />
          ))}
        </div>
      </div>
    </div>
  );
};

export default RegionDrillDown;
